var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React from 'react';
import { useStateWithMutators, useFetchPromise } from '@rad/hooks';
import { useNotifications, useNotify } from '@rad/contexts/notifications';
import { LocalDate } from '../../../utility/datetime';
import { formatInvoiceNumber } from '../../../utility/formatters';
import { SITE } from '../../../utility/constants';
import { requireToken } from '../../../utility/general';
import { postObjectJson } from '../../../utility/request';
import { getConnectorDejavoo } from '../../../http/connectors';
import { terminalName, fetchDejavooTerminals } from '../../../http/dejavoo';
import Button from '../button';
import CModal from '../modal';
import CInput from '../input';
import CSelect from '../select';
import CErrors from '../errors';
export default (props) => {
    var _a, _b, _c, _d, _e, _f, _g;
    const token = requireToken('merchant');
    const notifications = useNotifications('payment-apply');
    const notify = useNotify();
    const [dejavooStatus, setDejavooStatus] = React.useState(undefined);
    const [terminals] = useFetchPromise(fetchDejavooTerminals(token.merchant.id), []);
    const [terminalChoice, setTerminalChoice] = React.useState(null);
    const terminal = (_a = terminals === null || terminals === void 0 ? void 0 : terminals.find((t) => t.id === terminalChoice)) !== null && _a !== void 0 ? _a : terminals === null || terminals === void 0 ? void 0 : terminals.find((t) => t.is_default);
    const [state, mutators] = useStateWithMutators({
        'form': {
            'kind': 'cash',
            'date': LocalDate.now().toString(),
            'amount': ((_b = props.invoice) === null || _b === void 0 ? void 0 : _b.balance.noncard) || null,
            'reference': '',
        },
        'cardProcessing': {
            'method': 'terminal',
            'email': '',
            'mobile': '',
            'useExisting': true,
        },
        'errors': [],
    });
    const existingEmail = React.useMemo(() => { var _a, _b, _c, _d; return ((_d = (_c = (_b = (_a = props.invoice) === null || _a === void 0 ? void 0 : _a.client) === null || _b === void 0 ? void 0 : _b.contacts) === null || _c === void 0 ? void 0 : _c.find((x) => x.kind === 'email')) === null || _d === void 0 ? void 0 : _d.value) || null; }, [(_c = props.invoice) === null || _c === void 0 ? void 0 : _c.id]);
    const existingMobile = React.useMemo(() => { var _a, _b, _c, _d; return ((_d = (_c = (_b = (_a = props.invoice) === null || _a === void 0 ? void 0 : _a.client) === null || _b === void 0 ? void 0 : _b.contacts) === null || _c === void 0 ? void 0 : _c.find((x) => x.kind === 'phone' && x.subkind === 'mobile')) === null || _d === void 0 ? void 0 : _d.value) || null; }, [(_d = props.invoice) === null || _d === void 0 ? void 0 : _d.id]);
    const showAmountWarning = React.useMemo(() => {
        if (!props.invoice || state.form.amount === null) {
            return false;
        }
        if (props.refund) {
            return state.form.amount > props.invoice.balance.paid ? _jsx("div", { className: "alert mb-3 alert-warning", children: "Amount to refund is greater than the amounr paid." }) : undefined;
        }
        else {
            switch (state.form.kind) {
                case 'card':
                    return state.form.amount > props.invoice.balance.card ? _jsx("div", { className: "alert mb-3 alert-warning", children: "Amount to apply is greater than the balance." }) : undefined;
                default:
                    return state.form.amount > props.invoice.balance.noncard ? _jsx("div", { className: "alert mb-3 alert-warning", children: "Amount to apply is greater than the balance." }) : undefined;
            }
        }
    }, [props.invoice, state.form.amount, state.form.kind]);
    React.useEffect(() => {
        let mounted = true;
        let channel = null;
        const reloadStatus = () => {
            getConnectorDejavoo(token.merchant.id)
                .then((x) => { if (mounted) {
                setDejavooStatus(x);
            } })
                .catch(() => { if (mounted) {
                setDejavooStatus(null);
            } });
        };
        setDejavooStatus(undefined);
        reloadStatus();
        channel = new BroadcastChannel('DEJAVOO');
        channel.onmessage = reloadStatus;
        return () => {
            mounted = false;
            if (channel) {
                channel.close();
            }
        };
    }, [token.merchant.id]);
    React.useEffect(() => {
        if (!props.invoice) {
            return;
        }
        mutators.form.date(LocalDate.now().toString());
        mutators.form.kind('cash');
        mutators.form.amount(props.invoice.balance.noncard);
        mutators.form.reference('');
        mutators.cardProcessing.method('terminal');
        mutators.cardProcessing.email('');
        mutators.cardProcessing.mobile('');
        mutators.cardProcessing.useExisting(true);
        mutators.errors([]);
    }, [(_e = props.invoice) === null || _e === void 0 ? void 0 : _e.id]);
    React.useEffect(() => {
        if (!props.invoice) {
            return;
        }
        if (props.refund) {
            mutators.form.amount(props.invoice.balance.paid);
        }
        else {
            switch (state.form.kind) {
                case 'card':
                    mutators.form.amount(props.invoice.balance.card);
                    break;
                default:
                    mutators.form.amount(props.invoice.balance.noncard);
                    break;
            }
        }
    }, [state.form.kind, (_f = props.invoice) === null || _f === void 0 ? void 0 : _f.id]);
    const markPaid = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!props.invoice) {
            return;
        }
        yield postObjectJson(`/merchants/${token.merchant.id}/invoices/${props.invoice.id}/apply-payment`, {
            'kind': state.form.kind,
            'date': state.form.date,
            'amount': props.refund && !!state.form.amount ? -state.form.amount : state.form.amount,
            'reference': state.form.reference,
        }, {
            'error': (res) => __awaiter(void 0, void 0, void 0, function* () { return mutators.errors(yield res.json()); }),
            'success': () => __awaiter(void 0, void 0, void 0, function* () { return props.onClose(true); }),
        });
    });
    const handlePaymentError = (error) => {
        notify({
            'channel': 'payment-apply',
            'message': ((error === null || error === void 0 ? void 0 : error.includes('Terminal in use'))
                ? 'Terminal is in use. Please try again in a moment.'
                : error === 'Service Busy'
                    ? 'Terminal service is busy. Please try again in a few moments.'
                    : error || 'An unexpected error occurred'),
            'kind': 'error',
        });
    };
    const submit = (complete) => __awaiter(void 0, void 0, void 0, function* () {
        if (!props.invoice) {
            complete();
            return;
        }
        try {
            mutators.errors([]);
            if (props.refund || state.form.kind !== 'card' || !dejavooStatus || state.cardProcessing.method === 'external') {
                yield markPaid();
                return;
            }
            if (state.cardProcessing.method === 'email' && !state.cardProcessing.useExisting) {
                if (!state.cardProcessing.email) {
                    mutators.errors([{ 'field': 'email', 'message': 'Email is required' }]);
                    return;
                }
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(state.cardProcessing.email)) {
                    mutators.errors([{ 'field': 'email', 'message': 'Please enter a valid email address' }]);
                    return;
                }
            }
            if (state.cardProcessing.method === 'text' && !state.cardProcessing.useExisting) {
                if (!state.cardProcessing.mobile) {
                    mutators.errors([{ 'field': 'mobile', 'message': 'Mobile number is required' }]);
                    return;
                }
                const mobileRegex = /^1\d{10}$/;
                if (!mobileRegex.test(state.cardProcessing.mobile)) {
                    mutators.errors([{ 'field': 'mobile', 'message': 'Mobile number must start with 1 and contain 11 digits total' }]);
                    return;
                }
            }
            if (state.cardProcessing.method === 'terminal') {
                const clearWaiting = notify({ 'message': 'Waiting for card...', 'channel': 'payment-apply', 'kind': 'info', 'timeout': null });
                try {
                    yield postObjectJson(`/merchants/${token.merchant.id}/invoices/${props.invoice.id}/process-terminal${terminal ? `?terminal=${terminal.id}` : ''}`, {
                        'amount': state.form.amount,
                    }, {
                        'success': () => {
                            notify({ 'message': 'Payment successful!', 'kind': 'success' });
                            props.onClose(true);
                        },
                        'error': (res) => __awaiter(void 0, void 0, void 0, function* () {
                            if (res.status === 500) {
                                handlePaymentError('Server error occurred. Please try again.');
                            }
                            else {
                                try {
                                    const x = yield res.json();
                                    handlePaymentError((x === null || x === void 0 ? void 0 : x.error) || 'Payment processing failed');
                                }
                                catch (_a) {
                                    handlePaymentError('Unable to process payment');
                                }
                            }
                        }),
                    });
                }
                catch (_a) {
                    handlePaymentError('Connection error. Please try again.');
                }
                finally {
                    clearWaiting();
                }
                return;
            }
            const endpoint = state.cardProcessing.method === 'email'
                ? `/merchants/${token.merchant.id}/invoices/${props.invoice.id}/process-email`
                : `/merchants/${token.merchant.id}/invoices/${props.invoice.id}/process-text`;
            const contact = state.cardProcessing.useExisting
                ? (state.cardProcessing.method === 'email' ? existingEmail : existingMobile)
                : (state.cardProcessing.method === 'email' ? state.cardProcessing.email : state.cardProcessing.mobile);
            if (!contact) {
                mutators.errors([{
                        'field': state.cardProcessing.method === 'email' ? 'email' : 'mobile',
                        'message': `${state.cardProcessing.method === 'email' ? 'Email' : 'Mobile number'} is required`,
                    }]);
                return;
            }
            yield postObjectJson(endpoint, {
                'amount': state.form.amount,
                'contact': contact,
                'site': SITE,
            }, {
                'success': (response) => __awaiter(void 0, void 0, void 0, function* () {
                    let successMessage = state.cardProcessing.method === 'email'
                        ? 'Payment link sent via email!'
                        : 'Payment link sent via text message!';
                    try {
                        yield navigator.clipboard.writeText(response.payment_link);
                        successMessage += ' Link copied to clipboard.';
                    }
                    catch (err) {
                        console.error('Failed to copy to clipboard:', err);
                    }
                    notify({ 'message': successMessage, 'kind': 'success' });
                    props.onClose(true);
                }),
                'error': (res) => __awaiter(void 0, void 0, void 0, function* () {
                    if (res.status === 500) {
                        handlePaymentError('Server error occurred. Please try again.');
                        return;
                    }
                    try {
                        const x = yield res.json();
                        if (x === null || x === void 0 ? void 0 : x.errors) {
                            mutators.errors(x.errors);
                        }
                        else {
                            handlePaymentError((x === null || x === void 0 ? void 0 : x.error) || 'Payment processing failed');
                        }
                    }
                    catch (_a) {
                        handlePaymentError('Unable to process payment');
                    }
                }),
            });
        }
        catch (_b) {
            handlePaymentError('Connection error. Please try again.');
        }
        finally {
            complete();
        }
    });
    const [NOUN, VERB, METHODS] = (props.refund
        ? ['Refund', 'Refunded', [
                ['cash', 'Cash'],
                ['card', 'Card'],
                ['check', 'Check'],
                ['other', 'Other'],
            ]]
        : ['Payment', 'Paid', [
                ['cash', 'Cash'],
                ['card', 'Card'],
                ['check', 'Check'],
                ['other', 'Other'],
                ['write-off', 'Write Off'],
            ]]);
    return _jsx(_Fragment, { children: _jsxs(CModal, { visible: !!props.invoice, title: `Apply ${NOUN} - ${formatInvoiceNumber(props.invoice)}`, onClose: () => props.onClose(false), onSubmit: submit, footer: (loading) => _jsxs("div", { children: [_jsx(Button, { color: "secondary", me: 2, onClick: () => props.onClose(false), disabled: loading, children: "Cancel" }), _jsx(Button, { submit: true, color: props.refund ? 'danger' : 'primary', disabled: loading || !!notifications.length || !state.form.amount, children: loading
                            ? 'Processing...'
                            : props.refund || state.form.kind !== 'card' || !dejavooStatus
                                ? `Apply ${NOUN}`
                                : state.cardProcessing.method === 'terminal'
                                    ? 'Send to Terminal'
                                    : state.cardProcessing.method === 'email'
                                        ? `Send Email Link`
                                        : state.cardProcessing.method === 'text'
                                            ? 'Send Text Link'
                                            : 'Submit' })] }), children: [_jsx(CErrors, { errors: state.errors, mapping: { 'kind': `${NOUN} Method`, 'date': `Date ${VERB}`, 'amount': 'Amount' } }), !!((_g = props.invoice) === null || _g === void 0 ? void 0 : _g.payment_link) && _jsxs("div", { className: "alert mb-3 alert-warning", children: ["Invoice has a payment link on file: ", _jsx("a", { href: props.invoice.payment_link, children: "sent payment link" })] }), showAmountWarning, _jsxs(CInput, { type: "date", value: state.form.date, onChange: mutators.form.date, required: true, children: ["Date ", VERB] }), _jsxs(CSelect, { items: METHODS, valuef: (x) => x[0], titlef: (x) => x[1], value: state.form.kind, onChange: (x) => mutators.form.kind((x || 'cash')), required: true, children: [NOUN, " Method"] }), _jsx(CInput, { type: "number", value: state.form.amount, onChange: mutators.form.amount, prepend: props.refund ? '$' : '$', required: true, children: "Amount" }), state.form.kind === 'check' && _jsx(CInput, { type: "text", value: state.form.reference, onChange: mutators.form.reference, children: "Check #" }), state.form.kind === 'other' && _jsx(CInput, { type: "text", value: state.form.reference, onChange: mutators.form.reference, children: "Description" }), props.refund && state.form.kind === 'card' && _jsx("div", { className: "alert mb-3 alert-info", children: "This will only apply a refund to the invoice and not the actual card. Refunds and voids take place on the card terminal. If the transaction is from today and has not settled, use the void option. If the transaction has settled, use the refund option. Card numbers are required for a refund. Contact us for assistance." }), !props.refund && state.form.kind === 'card' && !!dejavooStatus && _jsxs(_Fragment, { children: [_jsx(CSelect, { items: [
                                ['terminal', 'Send to Terminal'],
                                ['email', 'Email Payment Link'],
                                ['text', 'Text Payment Link'],
                                ['external', 'Already Charged'],
                            ], valuef: (x) => x[0], titlef: (x) => x[1], value: state.cardProcessing.method, onChange: (x) => mutators.cardProcessing.method((x || 'terminal')), required: true, children: "Card Payment Option" }), state.cardProcessing.method === 'terminal' && !!terminals && terminals.length > 1 && !!terminal &&
                            _jsx(CSelect, { items: terminals, valuef: (x) => x.id, titlef: (x) => terminalName(x), value: terminal.id, onChange: (x) => setTerminalChoice(x), required: true, children: "Terminal" }), state.cardProcessing.method === 'email' && _jsxs("div", { className: "mb-3", children: [_jsx("label", { className: "form-label", children: "Customer Email" }), !!existingEmail && _jsxs("div", { className: "mb-2", children: [_jsxs("div", { className: "form-check", children: [_jsx("input", { className: "form-check-input", type: "radio", checked: state.cardProcessing.useExisting, onChange: () => { mutators.cardProcessing.useExisting(true); mutators.cardProcessing.email(''); }, id: "useExistingEmail" }), _jsxs("label", { className: "form-check-label", htmlFor: "useExistingEmail", children: ["Use existing: ", existingEmail] })] }), _jsxs("div", { className: "form-check", children: [_jsx("input", { className: "form-check-input", type: "radio", checked: !state.cardProcessing.useExisting, onChange: () => { mutators.cardProcessing.useExisting(false); mutators.cardProcessing.email(''); }, id: "useNewEmail" }), _jsx("label", { className: "form-check-label", htmlFor: "useNewEmail", children: "Enter new email" })] })] }), (!existingEmail || !state.cardProcessing.useExisting) &&
                                    _jsx(CInput, { type: "email", value: state.cardProcessing.email, onChange: (x) => { mutators.cardProcessing.email(x); mutators.cardProcessing.useExisting(false); }, required: true, children: "Enter Email Address" })] }), state.cardProcessing.method === 'text' && _jsxs("div", { className: "mb-3", children: [_jsx("label", { className: "form-label", children: "Customer Mobile Number" }), !!existingMobile && _jsxs("div", { className: "mb-2", children: [_jsxs("div", { className: "form-check", children: [_jsx("input", { className: "form-check-input", type: "radio", checked: state.cardProcessing.useExisting, onChange: () => { mutators.cardProcessing.useExisting(true); mutators.cardProcessing.mobile(''); }, id: "useExistingMobile" }), _jsxs("label", { className: "form-check-label", htmlFor: "useExistingMobile", children: ["Use existing: ", existingMobile] })] }), _jsxs("div", { className: "form-check", children: [_jsx("input", { className: "form-check-input", type: "radio", checked: !state.cardProcessing.useExisting, onChange: () => { mutators.cardProcessing.useExisting(false); mutators.cardProcessing.mobile(''); }, id: "useNewMobile" }), _jsx("label", { className: "form-check-label", htmlFor: "useNewMobile", children: "Enter new mobile number" })] })] }), (!existingMobile || !state.cardProcessing.useExisting) &&
                                    _jsx(CInput, { type: "tel", value: state.cardProcessing.mobile, onChange: (x) => { mutators.cardProcessing.mobile(x); mutators.cardProcessing.useExisting(false); }, required: true, children: "Ten digit phone number must include a 1 at the beginning with no spaces" })] }), notifications.map((x) => x.element)] })] }) });
};
